import { colors } from 'styles/colors'
import { memo, useEffect, useState } from 'react'
import Section from 'components/common/Section/Section';
import MagicIcon from 'icons/MagicIcon';
import { FormProvider, Resolver, useForm, useFormContext } from 'react-hook-form';
import { useAnalyzeProductInfo } from 'hooks/useAnalyzer';
import { useProductContext } from 'context/ProductContext';
import { productParticularsSchema, updateProductInfoDefaultValues, UpdateProductInfoFormValues } from 'pages/product/CreateProductPage/updateProductInfoValidationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUpdateProductInfo } from 'hooks/useProduct';
import { snackbarService } from 'services/snackbarService';
import SaveIcon from 'icons/SaveIcon';
import PencilIcon from 'icons/PencilIcon';
import XIcon from 'icons/XIcon';
import { Box } from '@mui/material';
import { mapProductInfoToForm } from 'pages/product/CreateProductPage/productMapper';
import ProductInformationFormDetails from './ProductInformationFormDetails';
import ProductInformationForm from './ProductInformationForm';


function ProductInformationDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();

  const methods = useForm<UpdateProductInfoFormValues>({
    resolver: yupResolver(productParticularsSchema) as Resolver<UpdateProductInfoFormValues>,
    defaultValues: updateProductInfoDefaultValues,
  });

  const {
    reset,
    getValues,
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods;

  useEffect(() => {
    reset(mapProductInfoToForm(product));
  }, [isEditing, product])

  const {
    refetch: refetchInfo,
    isFetching,
  } = useAnalyzeProductInfo(product.suggestion.id, {
    enabled: false,
    onSuccess: (res: any) => {
      reset({
        ...getValues(),
        ...res,
      });
      setIsEditing(true);
    },
    onError: (error: any) => {
      console.log('error', error);
    },
    select: (res: any) => res.data,
  });

  const { mutate: updateMutation, isLoading } = useUpdateProductInfo(
    (data: any) => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product information has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => { },
  )

  const onSubmit = (values: UpdateProductInfoFormValues) => {
    // The two descriptions are written in their own section and are not sent
    // from here at all: a field this form never showed is one it has nothing to
    // say about, and the endpoint leaves out what it is not sent.
    const { description: _ar, ...ar } = values.translations.ar;
    const { description: _en, ...en } = values.translations.en;

    updateMutation({
      id: product.id,
      ...values,
      translations: { ar, en },
    });
  };

  // What the form refuses to send, said out loud. Some of what this schema
  // checks is not on this form at all - the two descriptions are edited in their
  // own section - so a failure there left the Save button doing nothing at all
  // with nothing on screen to explain it.
  const onInvalid = (errors: Record<string, any>) => {
    const named: string[] = [];
    const walk = (node: any, path: string) => {
      if (!node || typeof node !== 'object') return;
      if (typeof node.message === 'string') return void named.push(`${path}: ${node.message}`);
      Object.entries(node).forEach(([key, value]) => walk(value, path ? `${path}.${key}` : key));
    };
    walk(errors, '');

    snackbarService.showSnackbar(
      named.length ? `Not saved - ${named.join('; ')}` : 'Not saved - the form is incomplete',
      'error',
    );
  };

  return (
    <FormProvider {...methods}>
      <Section
        title={'Product Information'}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <MagicIcon size={20} color={colors.primary} />,
                title: 'Analyze',
                onClick: () => refetchInfo(),
                loading: isFetching,
                disabled: isLoading,
              },
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: handleSubmit(onSubmit, onInvalid),
                loading: isLoading,
                disabled: isFetching,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => setIsEditing(false),
                disabled: isFetching || isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => setIsEditing((prev) => !prev),
                disabled: isFetching || isLoading,
              }
            ]),
        ]}
        content={isEditing ? <ProductInformationForm /> : <ProductInformationFormDetails />}
      />
    </FormProvider>
  )
}

export default memo(ProductInformationDetails);
